import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import {
    Bookmark,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Clock,
    Quote,
    Check,
    BarChart2,
    FileText
} from 'lucide-react';
import type { Paper } from '../types';
import { formatAuthors } from '../utils/authors';
import { generateBibTeX } from '../utils/bibtex';

interface PaperCardProps {
    paper: Paper;
    isBookmarked: boolean;
    onToggleBookmark: (p: Paper) => void;
}

export const PaperCard = ({ paper, isBookmarked, onToggleBookmark }: PaperCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [bibtexCopied, setBibtexCopied] = useState(false);

    const abstract = paper.abstract || "No abstract available for this work.";
    // We use a length check to decide if we show the "More" button
    const isLongAbstract = abstract.length > 250;

    const copyCitation = () => {
        // Strip HTML tags for the plain text citation
        const cleanTitle = paper.title.replace(/<[^>]+>/g, '');
        const venueText = paper.venue ? ` ${paper.venue}.` : '';
        const citation = `${formatAuthors(paper.authorships)} (${paper.publication_year}). ${cleanTitle}.${venueText} ${paper.doi || ''}`;

        // Robust fallback for copying text
        const textArea = document.createElement("textarea");
        textArea.value = citation;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
        }
        document.body.removeChild(textArea);
    };

    const copyBibTeX = () => {
        const bibtex = generateBibTeX(paper);
        const textArea = document.createElement("textarea");
        textArea.value = bibtex;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            setBibtexCopied(true);
            setTimeout(() => setBibtexCopied(false), 2000);
        } catch (err) {
            console.error('Fallback: Oops, unable to copy BibTeX', err);
        }
        document.body.removeChild(textArea);
    };

    return (
        <div className="group relative bg-white dark:bg-slate-800 rounded-xl p-4 sm:p-5 md:p-6 shadow-sm hover:shadow-md active:shadow-lg border border-slate-100 dark:border-slate-700 transition-all duration-200 flex flex-col h-full touch-manipulation">
            <div className="absolute top-3 sm:top-4 right-3 sm:right-4 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10 flex gap-2">
                <button
                    onClick={copyBibTeX}
                    className="p-2.5 sm:p-2 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 active:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 border border-slate-100 dark:border-slate-700 rounded-full transition-all shadow-sm touch-manipulation"
                    title="Copy BibTeX"
                >
                    {bibtexCopied ? <Check size={16} className="text-emerald-500" /> : <FileText size={16} />}
                </button>
                <button
                    onClick={copyCitation}
                    className="p-2.5 sm:p-2 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 active:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 border border-slate-100 dark:border-slate-700 rounded-full transition-all shadow-sm touch-manipulation"
                    title="Copy Citation"
                >
                    {copied ? <Check size={16} className="text-emerald-500" /> : <Quote size={16} />}
                </button>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <span className={`px-2 py-1 rounded-md ${paper.type === 'preprint' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {paper.type === 'preprint' ? 'Preprint' : 'Journal'}
                </span>
                <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {paper.publication_year}
                </span>
                {paper.cited_by_count > 0 && (
                    <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                        <BarChart2 size={12} />
                        Cited by {paper.cited_by_count}
                    </span>
                )}
            </div>

            {/* Explicit Journal/Venue Name Display */}
            <div className="mb-2 text-xs font-bold text-indigo-600 tracking-wide">
                {paper.venue}
            </div>

            <h3 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100 leading-tight mb-2 pr-10 sm:pr-8">
                <a
                    href={paper.doi || paper.primary_location?.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(paper.title) }}
                />
            </h3>
            <div className="text-sm text-slate-500 dark:text-slate-400 mb-2 font-medium">
                {formatAuthors(paper.authorships)}
            </div>

            {/* Institution Hint (first one found) */}
            {paper.institutions && paper.institutions.length > 0 && (
                <div className="text-xs text-slate-400 dark:text-slate-500 mb-4 truncate">
                    {paper.institutions.slice(0, 2).join(', ')}
                </div>
            )}

            <div className="mb-4 flex-grow">
                <div
                    className={`text-sm text-slate-600 dark:text-slate-300 leading-relaxed ${expanded ? '' : 'line-clamp-4'}`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(abstract) }}
                />
                {isLongAbstract && (
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="mt-2 text-indigo-600 font-medium hover:underline active:text-indigo-700 inline-flex items-center gap-0.5 text-xs uppercase tracking-wide py-1 touch-manipulation"
                    >
                        {expanded ? <><ChevronUp size={12} /> Less</> : <><ChevronDown size={12} /> Read More</>}
                    </button>
                )}
            </div>

            <div className="mt-auto pt-4 border-t border-slate-50 dark:border-slate-700 flex items-center justify-between">
                <div className="flex flex-wrap gap-2 sm:gap-3">
                    {(paper.primary_location?.pdf_url || paper.open_access?.oa_url) && (
                        <a
                            href={paper.primary_location?.pdf_url || paper.open_access?.oa_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/50 px-3 py-2 sm:py-1.5 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900 active:bg-indigo-200 transition-colors touch-manipulation"
                        >
                            <ExternalLink size={12} />
                            Read PDF
                        </a>
                    )}
                    {paper.doi && (
                        <a
                            href={paper.doi}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700 px-3 py-2 sm:py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-600 active:bg-slate-200 transition-colors touch-manipulation"
                        >
                            DOI
                        </a>
                    )}
                </div>

                <button
                    onClick={() => onToggleBookmark(paper)}
                    className={`p-2.5 sm:p-2 rounded-full transition-all duration-200 touch-manipulation ${isBookmarked ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-500' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 hover:text-slate-600 dark:hover:text-slate-300'}`}
                >
                    <Bookmark size={18} fill={isBookmarked ? "currentColor" : "none"} />
                </button>
            </div>
        </div>
    );
};
